import {
  ArrowRight,
  Brain,
  Building2,
  ClipboardCheck,
  Code2,
  Cpu,
  Layers,
  Lock,
  MessageCircle,
  Server,
  Shield,
  Sparkles,
  TrendingUp,
  Truck,
  UtensilsCrossed,
  Zap,
} from "lucide-react";

import { DemoRequestForm } from "@/components/DemoRequestForm";

export default function Home() {
  return (
    <main>
      {/* Hero */}
      <header className="relative pt-32 pb-20 lg:pt-48 lg:pb-32 overflow-hidden bg-[#09090b] mesh-bg">
        <div className="container mx-auto px-6 relative z-10 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-violet-500/10 border border-violet-500/20 text-violet-300 text-xs font-semibold mb-6 uppercase tracking-wider">
            <Sparkles className="w-3.5 h-3.5" />
            AI-Powered Vertical Operating Systems
          </div>

          <h1 className="text-5xl lg:text-7xl font-bold font-display text-white leading-tight mb-6 max-w-5xl mx-auto">
            We build the{" "}
            <span className="text-gradient">operating systems</span> that power
            industries.
          </h1>

          <p className="text-xl text-zinc-300 mb-10 max-w-2xl mx-auto leading-relaxed">
            Furistic creates AI-native platforms for complex industries. Deep
            domain expertise meets cutting-edge intelligence — so your team can
            focus on what matters.
          </p>

          <div className="flex flex-col sm:flex-row justify-center gap-4 mb-20">
            <a
              href="#products"
              className="bg-violet-600 hover:bg-violet-700 text-white px-8 py-4 rounded-full text-lg font-semibold transition shadow-xl shadow-violet-600/25 flex items-center justify-center gap-2"
            >
              Explore Products <ArrowRight className="w-5 h-5" />
            </a>
            <a
              href="#platform"
              className="glass-panel text-white hover:bg-white/10 px-8 py-4 rounded-full text-lg font-semibold transition flex items-center justify-center gap-2"
            >
              <Layers className="w-5 h-5" /> The Platform
            </a>
          </div>
        </div>
      </header>

      {/* Products */}
      <section id="products" className="py-24 bg-white dark:bg-[#09090b]">
        <div className="container mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-violet-500 font-bold tracking-wide uppercase text-sm mb-3">
              Products
            </h2>
            <h3 className="text-4xl font-bold font-display text-zinc-900 dark:text-zinc-100 mb-4">
              Vertical operating systems, built from the ground up.
            </h3>
            <p className="text-zinc-600 dark:text-zinc-400 text-lg">
              Each product is purpose-built for its industry — not a generic
              tool with a skin on top.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {/* RealtyOS */}
            <div className="group relative bg-zinc-50 dark:bg-zinc-900/50 p-10 rounded-3xl border border-zinc-200 dark:border-zinc-800 hover:border-blue-300 dark:hover:border-blue-500/30 transition-all hover:shadow-xl">
              <div className="w-16 h-16 bg-blue-100 dark:bg-blue-500/20 rounded-2xl flex items-center justify-center text-blue-600 dark:text-blue-400 mb-6 group-hover:scale-110 transition">
                <Building2 className="w-8 h-8" />
              </div>
              <h4 className="text-2xl font-bold text-zinc-900 dark:text-zinc-100 mb-3">
                RealtyOS
              </h4>
              <p className="text-zinc-600 dark:text-zinc-400 leading-relaxed mb-6">
                The intelligent operating system for real estate teams. CRM, an
                AI assistant, document intelligence, MLS integration, and
                compliance workflows — all in one platform.
              </p>
              <ul className="space-y-2 mb-8">
                <li className="flex items-center text-zinc-600 dark:text-zinc-400 text-sm">
                  <Zap className="w-4 h-4 text-blue-500 mr-2 shrink-0" />{" "}
                  AI-powered lead capture &amp; qualification
                </li>
                <li className="flex items-center text-zinc-600 dark:text-zinc-400 text-sm">
                  <Zap className="w-4 h-4 text-blue-500 mr-2 shrink-0" /> Smart
                  document extraction &amp; calendar sync
                </li>
                <li className="flex items-center text-zinc-600 dark:text-zinc-400 text-sm">
                  <Zap className="w-4 h-4 text-blue-500 mr-2 shrink-0" />{" "}
                  FINTRAC-aligned workflows &amp; CREA DDF support
                </li>
              </ul>
              <a
                href="https://getrealtyos.com"
                className="inline-flex items-center gap-2 text-blue-500 font-semibold hover:text-blue-600 dark:hover:text-blue-400 transition text-sm"
              >
                Visit getrealtyos.com <ArrowRight className="w-4 h-4" />
              </a>
            </div>

            {/* TradeOS */}
            <div className="group relative bg-zinc-50 dark:bg-zinc-900/50 p-10 rounded-3xl border border-zinc-200 dark:border-zinc-800 hover:border-emerald-300 dark:hover:border-emerald-500/30 transition-all hover:shadow-xl">
              <div className="w-16 h-16 bg-emerald-100 dark:bg-emerald-500/20 rounded-2xl flex items-center justify-center text-emerald-600 dark:text-emerald-400 mb-6 group-hover:scale-110 transition">
                <TrendingUp className="w-8 h-8" />
              </div>
              <h4 className="text-2xl font-bold text-zinc-900 dark:text-zinc-100 mb-3">
                TradeOS
              </h4>
              <p className="text-zinc-600 dark:text-zinc-400 leading-relaxed mb-6">
                A research engine that executes trades. Strategy backtesting
                with vectorized and event-driven engines, AI research
                conversations, and risk controls embedded in the order flow.
              </p>
              <ul className="space-y-2 mb-8">
                <li className="flex items-center text-zinc-600 dark:text-zinc-400 text-sm">
                  <Zap className="w-4 h-4 text-emerald-500 mr-2 shrink-0" />{" "}
                  Dual-engine backtesting &amp; strategy validation
                </li>
                <li className="flex items-center text-zinc-600 dark:text-zinc-400 text-sm">
                  <Zap className="w-4 h-4 text-emerald-500 mr-2 shrink-0" /> AI
                  research assistant with source attribution
                </li>
                <li className="flex items-center text-zinc-600 dark:text-zinc-400 text-sm">
                  <Zap className="w-4 h-4 text-emerald-500 mr-2 shrink-0" />{" "}
                  Risk-first controls embedded in the order flow
                </li>
              </ul>
              <a
                href="https://gettradeos.com"
                className="inline-flex items-center gap-2 text-emerald-500 font-semibold hover:text-emerald-600 dark:hover:text-emerald-400 transition text-sm"
              >
                Visit gettradeos.com <ArrowRight className="w-4 h-4" />
              </a>
            </div>

            {/* ConformOS */}
            <div className="group relative bg-zinc-50 dark:bg-zinc-900/50 p-10 rounded-3xl border border-zinc-200 dark:border-zinc-800 hover:border-amber-300 dark:hover:border-amber-500/30 transition-all hover:shadow-xl">
              <div className="w-16 h-16 bg-amber-100 dark:bg-amber-500/20 rounded-2xl flex items-center justify-center text-amber-600 dark:text-amber-400 mb-6 group-hover:scale-110 transition">
                <ClipboardCheck className="w-8 h-8" />
              </div>
              <h4 className="text-2xl font-bold text-zinc-900 dark:text-zinc-100 mb-3">
                ConformOS
              </h4>
              <p className="text-zinc-600 dark:text-zinc-400 leading-relaxed mb-6">
                A workspace for standards-audit automation. Evidence intake and
                indexing, clause-by-clause audit work, human review, and
                gap/corrective-action tracking.
              </p>
              <ul className="space-y-2 mb-8">
                <li className="flex items-center text-zinc-600 dark:text-zinc-400 text-sm">
                  <Zap className="w-4 h-4 text-amber-500 mr-2 shrink-0" />{" "}
                  Evidence intake and indexing
                </li>
                <li className="flex items-center text-zinc-600 dark:text-zinc-400 text-sm">
                  <Zap className="w-4 h-4 text-amber-500 mr-2 shrink-0" />{" "}
                  Clause-by-clause audit workbench
                </li>
                <li className="flex items-center text-zinc-600 dark:text-zinc-400 text-sm">
                  <Zap className="w-4 h-4 text-amber-500 mr-2 shrink-0" />{" "}
                  Audit, gap, and corrective-action reports
                </li>
              </ul>
              <span className="inline-flex items-center gap-2 text-amber-600 dark:text-amber-400 font-semibold text-sm">
                Standards audits, in one workspace
              </span>
            </div>

            {/* Haulyard */}
            <div className="group relative bg-zinc-50 dark:bg-zinc-900/50 p-10 rounded-3xl border border-zinc-200 dark:border-zinc-800 hover:border-cyan-300 dark:hover:border-cyan-500/30 transition-all hover:shadow-xl">
              <div className="w-16 h-16 bg-cyan-100 dark:bg-cyan-500/20 rounded-2xl flex items-center justify-center text-cyan-600 dark:text-cyan-400 mb-6 group-hover:scale-110 transition">
                <Truck className="w-8 h-8" />
              </div>
              <h4 className="text-2xl font-bold text-zinc-900 dark:text-zinc-100 mb-3">
                Haulyard
              </h4>
              <p className="text-zinc-600 dark:text-zinc-400 leading-relaxed mb-6">
                The operations command center for waste &amp; environmental
                services — one system in place of the routing, compliance,
                accounting, and telematics stack most haulers run today.
              </p>
              <ul className="space-y-2 mb-8">
                <li className="flex items-center text-zinc-600 dark:text-zinc-400 text-sm">
                  <Zap className="w-4 h-4 text-cyan-500 mr-2 shrink-0" />{" "}
                  Dispatch and routing for fleets
                </li>
                <li className="flex items-center text-zinc-600 dark:text-zinc-400 text-sm">
                  <Zap className="w-4 h-4 text-cyan-500 mr-2 shrink-0" />{" "}
                  Compliance and accounting in one system
                </li>
                <li className="flex items-center text-zinc-600 dark:text-zinc-400 text-sm">
                  <Zap className="w-4 h-4 text-cyan-500 mr-2 shrink-0" /> Native
                  iOS/Android apps for drivers
                </li>
              </ul>
              <span className="inline-flex items-center gap-2 text-cyan-600 dark:text-cyan-400 font-semibold text-sm">
                Waste &amp; environmental operations
              </span>
            </div>

            {/* Foodium */}
            <div className="group relative bg-zinc-50 dark:bg-zinc-900/50 p-10 rounded-3xl border border-zinc-200 dark:border-zinc-800 hover:border-rose-300 dark:hover:border-rose-500/30 transition-all hover:shadow-xl">
              <div className="w-16 h-16 bg-rose-100 dark:bg-rose-500/20 rounded-2xl flex items-center justify-center text-rose-600 dark:text-rose-400 mb-6 group-hover:scale-110 transition">
                <UtensilsCrossed className="w-8 h-8" />
              </div>
              <h4 className="text-2xl font-bold text-zinc-900 dark:text-zinc-100 mb-3">
                Foodium
              </h4>
              <p className="text-zinc-600 dark:text-zinc-400 leading-relaxed mb-6">
                The restaurants vertical: phone and chat pickup/delivery
                ordering on the platform&apos;s conversational core, with a
                restaurant dashboard for menus and live orders.
              </p>
              <ul className="space-y-2 mb-8">
                <li className="flex items-center text-zinc-600 dark:text-zinc-400 text-sm">
                  <Zap className="w-4 h-4 text-rose-500 mr-2 shrink-0" /> Phone
                  + chat ordering on the conversational core
                </li>
                <li className="flex items-center text-zinc-600 dark:text-zinc-400 text-sm">
                  <Zap className="w-4 h-4 text-rose-500 mr-2 shrink-0" /> Menu
                  editor with AI import review
                </li>
                <li className="flex items-center text-zinc-600 dark:text-zinc-400 text-sm">
                  <Zap className="w-4 h-4 text-rose-500 mr-2 shrink-0" /> Live
                  orders, delivery policy, and call transcripts
                </li>
              </ul>
              <span className="inline-flex items-center gap-2 text-rose-600 dark:text-rose-400 font-semibold text-sm">
                Pickup &amp; delivery for restaurants
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Platform */}
      <section id="platform" className="py-24 bg-zinc-50 dark:bg-[#050506]">
        <div className="container mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-violet-500 font-bold tracking-wide uppercase text-sm mb-3">
              The Platform
            </h2>
            <h3 className="text-4xl font-bold font-display text-zinc-900 dark:text-zinc-100 mb-4">
              One AI engine. Multiple industries.
            </h3>
            <p className="text-zinc-600 dark:text-zinc-400 text-lg">
              Every Furistic product is powered by the same core platform —
              shared intelligence that gets smarter across every vertical.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="bg-white dark:bg-zinc-900/50 p-8 rounded-2xl border border-zinc-200 dark:border-zinc-800 hover:shadow-lg transition-all group">
              <div className="w-14 h-14 bg-violet-100 dark:bg-violet-500/20 rounded-xl flex items-center justify-center text-violet-600 dark:text-violet-400 mb-6 group-hover:scale-110 transition">
                <Brain className="w-7 h-7" />
              </div>
              <h4 className="text-xl font-bold text-zinc-900 dark:text-zinc-100 mb-3">
                AI Engine &amp; Otto
              </h4>
              <p className="text-zinc-600 dark:text-zinc-400 leading-relaxed">
                Multi-model orchestration with domain-specific grounding: RAG
                pipelines, tool calling, and product-aware agents. Otto, the
                shared AI assistant, answers with each product&apos;s own tools
                — in chat, on the phone, and on this site.
              </p>
            </div>

            <div className="bg-white dark:bg-zinc-900/50 p-8 rounded-2xl border border-zinc-200 dark:border-zinc-800 hover:shadow-lg transition-all group">
              <div className="w-14 h-14 bg-blue-100 dark:bg-blue-500/20 rounded-xl flex items-center justify-center text-blue-600 dark:text-blue-400 mb-6 group-hover:scale-110 transition">
                <Shield className="w-7 h-7" />
              </div>
              <h4 className="text-xl font-bold text-zinc-900 dark:text-zinc-100 mb-3">
                Identity &amp; Auth
              </h4>
              <p className="text-zinc-600 dark:text-zinc-400 leading-relaxed">
                Shared identity layer across all products. Multi-tenant,
                role-based access, organization SSO, OAuth 2.0, and fine-grained
                permissions.
              </p>
            </div>

            <div className="bg-white dark:bg-zinc-900/50 p-8 rounded-2xl border border-zinc-200 dark:border-zinc-800 hover:shadow-lg transition-all group">
              <div className="w-14 h-14 bg-pink-100 dark:bg-pink-500/20 rounded-xl flex items-center justify-center text-pink-600 dark:text-pink-400 mb-6 group-hover:scale-110 transition">
                <Server className="w-7 h-7" />
              </div>
              <h4 className="text-xl font-bold text-zinc-900 dark:text-zinc-100 mb-3">
                Infrastructure
              </h4>
              <p className="text-zinc-600 dark:text-zinc-400 leading-relaxed">
                Self-hosted Kubernetes with infrastructure defined as code,
                full-stack observability, and staged rollouts. Marketing sites
                ship as static exports on Cloudflare&apos;s edge.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Developer Section */}
      <section id="developers" className="py-24 bg-white dark:bg-[#09090b]">
        <div className="container mx-auto px-6">
          <div className="flex flex-col lg:flex-row items-center gap-16 max-w-5xl mx-auto">
            <div className="lg:w-1/2">
              <h2 className="text-violet-500 font-bold tracking-wide uppercase text-sm mb-3">
                For Developers
              </h2>
              <h3 className="text-3xl lg:text-4xl font-bold font-display text-zinc-900 dark:text-zinc-100 mb-6">
                Build on Furistic.
              </h3>
              <p className="text-zinc-600 dark:text-zinc-400 text-lg mb-8 leading-relaxed">
                Extend any Furistic product with our developer platform. REST
                APIs, webhooks, MCP tool servers, and API keys — everything you
                need to build custom integrations.
              </p>
              <ul className="space-y-4 mb-8">
                <li className="flex items-center text-zinc-700 dark:text-zinc-300">
                  <Code2 className="w-5 h-5 text-violet-500 mr-3 shrink-0" />{" "}
                  REST APIs behind a shared API gateway
                </li>
                <li className="flex items-center text-zinc-700 dark:text-zinc-300">
                  <Cpu className="w-5 h-5 text-violet-500 mr-3 shrink-0" /> MCP
                  tool server protocol for AI agents
                </li>
                <li className="flex items-center text-zinc-700 dark:text-zinc-300">
                  <Lock className="w-5 h-5 text-violet-500 mr-3 shrink-0" />{" "}
                  OAuth 2.0, API keys, and signed webhooks
                </li>
                <li className="flex items-center text-zinc-700 dark:text-zinc-300">
                  <Zap className="w-5 h-5 text-violet-500 mr-3 shrink-0" />{" "}
                  Real-time event streams via WebSocket
                </li>
              </ul>
              <a
                href="#contact"
                className="inline-flex items-center gap-2 bg-violet-600 hover:bg-violet-700 text-white px-6 py-3 rounded-full font-semibold transition shadow-lg shadow-violet-600/30 text-sm"
              >
                Request API Access <ArrowRight className="w-4 h-4" />
              </a>
            </div>
            <div className="lg:w-1/2">
              <div className="bg-[#111113] rounded-xl border border-zinc-800 p-6 font-mono text-sm glow-violet">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-3 h-3 rounded-full bg-red-500/80" />
                  <div className="w-3 h-3 rounded-full bg-yellow-500/80" />
                  <div className="w-3 h-3 rounded-full bg-green-500/80" />
                  <span className="text-zinc-500 text-xs ml-2">
                    api.furistic.co · Illustrative example
                  </span>
                </div>
                <pre className="text-zinc-300 overflow-x-auto">
                  <code>
                    <span className="text-violet-400">GET</span>{" "}
                    /v1/ai/chat/completions{"\n"}
                    <span className="text-zinc-500">
                      Authorization: Bearer fur_sk_...
                    </span>
                    {"\n\n"}
                    <span className="text-zinc-500">{"{"}</span>
                    {"\n"}
                    {"  "}
                    <span className="text-blue-400">&quot;model&quot;</span>:{" "}
                    <span className="text-emerald-400">
                      &quot;furistic-domain-v3&quot;
                    </span>
                    ,{"\n"}
                    {"  "}
                    <span className="text-blue-400">&quot;messages&quot;</span>:
                    [{"\n"}
                    {"    "}
                    {"{"}{" "}
                    <span className="text-blue-400">&quot;role&quot;</span>:{" "}
                    <span className="text-emerald-400">&quot;user&quot;</span>,
                    {"\n"}
                    {"      "}
                    <span className="text-blue-400">&quot;content&quot;</span>:{" "}
                    <span className="text-emerald-400">
                      &quot;Analyze my portfolio risk&quot;
                    </span>{" "}
                    {"}"}
                    {"\n"}
                    {"  "}],{"\n"}
                    {"  "}
                    <span className="text-blue-400">&quot;tools&quot;</span>: [
                    <span className="text-emerald-400">
                      &quot;portfolio.analyze&quot;
                    </span>
                    ]{"\n"}
                    <span className="text-zinc-500">{"}"}</span>
                  </code>
                </pre>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-20 bg-violet-600 text-white">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-5xl font-bold mb-2">5</div>
              <p className="text-violet-100">Products</p>
            </div>
            <div>
              <div className="text-5xl font-bold mb-2">90+</div>
              <p className="text-violet-100">Shared Libraries</p>
            </div>
            <div>
              <div className="text-5xl font-bold mb-2">Edge</div>
              <p className="text-violet-100">Global Delivery</p>
            </div>
            <div>
              <div className="text-5xl font-bold mb-2">HTTPS</div>
              <p className="text-violet-100">Encrypted Transport</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section id="contact" className="py-20 bg-zinc-50 dark:bg-[#050506]">
        <div className="container mx-auto px-6">
          <div className="bg-[#09090b] rounded-3xl p-12 lg:p-20 text-center relative overflow-hidden">
            <div className="absolute inset-0 mesh-bg opacity-50" />
            <div className="relative z-10 max-w-3xl mx-auto">
              <Sparkles className="w-12 h-12 text-violet-400 mx-auto mb-6" />
              <h2 className="text-3xl lg:text-5xl font-bold font-display text-white mb-6">
                Ready to build something{" "}
                <span className="text-gradient-warm">extraordinary</span>?
              </h2>
              <p className="text-zinc-300 text-lg mb-10">
                Whether you&apos;re a brokerage, a trading desk, an audit team,
                a hauler, or a restaurant — we&apos;d love to talk.
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <a
                  href="#contact-form"
                  className="bg-violet-600 hover:bg-violet-700 text-white px-8 py-4 rounded-full text-lg font-semibold transition shadow-xl shadow-violet-600/25 flex items-center justify-center gap-2"
                >
                  Contact Us <ArrowRight className="w-5 h-5" />
                </a>
                <a
                  href="#developers"
                  className="glass-panel text-white hover:bg-white/10 px-8 py-4 rounded-full text-lg font-semibold transition flex items-center justify-center gap-2"
                >
                  <Code2 className="w-5 h-5" /> Developer Docs
                </a>
              </div>
              <div id="contact-form" className="mt-10 scroll-mt-24">
                <DemoRequestForm />
              </div>
              <p className="text-zinc-400 text-sm mt-8 flex items-center justify-center gap-2">
                <MessageCircle className="w-4 h-4 text-violet-400" /> Ask Otto,
                our AI assistant, using the chat button — anytime.
              </p>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
